import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import {
  ArrowLeft,
  Image,
  Film,
  Type,
  Mic,
  Eye,
  Clock,
  Check,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VoiceNotePlayer } from '@/components/VoiceNotePlayer';
import { getApiBaseUrl } from '@/lib/capacitor';
import { formatDate } from '@/lib/utils';

interface Post {
  _id: string;
  state: string;
  contentType: 'photo' | 'video' | 'hudText' | 'voiceNote';
  caption?: string;
  hudText?: string;
  originalFilename?: string;
  processedFilename?: string;
  voiceNoteFilename?: string;
  viewCount: number;
  reactionCount?: number;
  user: {
    name: string;
  };
  createdAt: string;
}

interface Reaction {
  _id: string;
  text: string;
  read: boolean;
  createdAt: string;
  author?: {
    _id: string;
    name: string;
    email?: string;
  };
}

const contentTypeIcons = {
  photo: Image,
  video: Film,
  hudText: Type,
  voiceNote: Mic,
};

const stateColors: Record<string, string> = {
  unread: 'bg-amber-500',
  published: 'bg-emerald-500',
  read: 'bg-blue-500',
  processing: 'bg-purple-500 animate-pulse',
  'processing-failed': 'bg-red-500',
  'publish-failed': 'bg-red-500',
};

export default function TimelinePage() {
  // Restore scroll position when returning to this page
  useScrollRestoration('timeline');

  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedReactions, setExpandedReactions] = useState<Record<string, boolean>>({});
  const [reactionsByPost, setReactionsByPost] = useState<Record<string, Reaction[]>>({});
  const [loadingReactionsByPost, setLoadingReactionsByPost] = useState<Record<string, boolean>>({});
  const [reactionErrorsByPost, setReactionErrorsByPost] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchPosts();
  }, []);

  // Listen for processing complete events to refresh the list
  useEffect(() => {
    const handleProcessingComplete = () => {
      console.log('[TimelinePage] Processing complete event received, refreshing posts');
      fetchPosts();
    };

    window.addEventListener('ping-processing-complete', handleProcessingComplete);
    return () => {
      window.removeEventListener('ping-processing-complete', handleProcessingComplete);
    };
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch('/api/posts');
      const data = await response.json();
      if (data.success) {
        setPosts(data.posts);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkRead = async (postId: string) => {
    try {
      const response = await fetch(`/api/posts/${postId}/read`, {
        method: 'PATCH',
      });
      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const toggleReactions = async (postId: string) => {
    const isExpanded = expandedReactions[postId];
    if (isExpanded) {
      setExpandedReactions((prev) => ({ ...prev, [postId]: false }));
      return;
    }

    setExpandedReactions((prev) => ({ ...prev, [postId]: true }));

    if (reactionsByPost[postId] || loadingReactionsByPost[postId]) {
      return;
    }

    setLoadingReactionsByPost((prev) => ({ ...prev, [postId]: true }));
    setReactionErrorsByPost((prev) => ({ ...prev, [postId]: '' }));

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/reactions/post/${postId}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load reactions');
      }

      setReactionsByPost((prev) => ({ ...prev, [postId]: data.reactions }));
    } catch (error) {
      console.error('Failed to fetch reactions:', error);
      setReactionErrorsByPost((prev) => ({
        ...prev,
        [postId]: 'Failed to load reactions',
      }));
    } finally {
      setLoadingReactionsByPost((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const getMediaUrl = (filename?: string) => {
    if (!filename) return null;
    return `${getApiBaseUrl()}/uploads/${filename}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/share">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="font-display font-semibold">Timeline</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {posts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No posts yet
          </div>
        ) : (
          posts.map((post) => {
            const Icon = contentTypeIcons[post.contentType];
            const mediaUrl = getMediaUrl(
              post.processedFilename || post.originalFilename
            );
            const voiceNoteUrl = getMediaUrl(post.voiceNoteFilename);
            const reactionCount = post.reactionCount ?? 0;
            const isReactionSectionExpanded = expandedReactions[post._id];
            const reactions = reactionsByPost[post._id] || [];
            const isLoadingReactions = loadingReactionsByPost[post._id];
            const reactionError = reactionErrorsByPost[post._id];

            return (
              <div
                key={post._id}
                className="rounded-xl border border-border bg-card overflow-hidden"
              >
                {/* Media preview */}
                {post.contentType === 'photo' && mediaUrl && (
                  <img
                    src={mediaUrl}
                    alt=""
                    className="w-full h-48 object-cover"
                  />
                )}
                {post.contentType === 'video' && mediaUrl && (
                  <video
                    src={mediaUrl}
                    className="w-full h-48 object-cover"
                    muted
                  />
                )}

                {/* Voice note only - show player prominently */}
                {post.contentType === 'voiceNote' && voiceNoteUrl && (
                  <div className="p-4 bg-muted/30">
                    <VoiceNotePlayer src={voiceNoteUrl} />
                  </div>
                )}

                {/* Content */}
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{post.user.name.split(' ')[0]}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatDate(post.createdAt)}
                        </div>
                      </div>
                    </div>

                    {/* State badge */}
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          stateColors[post.state] || 'bg-muted'
                        }`}
                      />
                      <span className="text-xs text-muted-foreground capitalize">
                        {post.state.replace('-', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Text content */}
                  {(post.caption || post.hudText) && (
                    <p className="text-sm">{post.caption || post.hudText}</p>
                  )}

                  {/* Voice note attached to photo/video/hudText */}
                  {post.contentType !== 'voiceNote' && voiceNoteUrl && (
                    <VoiceNotePlayer src={voiceNoteUrl} />
                  )}

                  {/* Footer */}
                  <div className="space-y-3 pt-2 border-t border-border">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {post.viewCount} views
                        </div>
                        {reactionCount > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleReactions(post._id)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                          >
                            <MessageCircle className="w-3 h-3" />
                            {reactionCount} reactions
                            {isReactionSectionExpanded ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>

                      {(post.state === 'unread' || post.state === 'published') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkRead(post._id)}
                          className="text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Mark read
                        </Button>
                      )}
                    </div>

                    {isReactionSectionExpanded && (
                      <div className="rounded-lg border border-border bg-muted/20 p-3">
                        {isLoadingReactions ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading reactions...
                          </div>
                        ) : reactionError ? (
                          <p className="text-sm text-destructive">{reactionError}</p>
                        ) : reactions.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No reactions yet</p>
                        ) : (
                          <div className="space-y-3">
                            {reactions.map((reaction) => (
                              <div
                                key={reaction._id}
                                className="rounded-lg border border-border bg-background px-3 py-2.5"
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-sm font-medium text-foreground">
                                    {reaction.author?.name || 'Someone'}
                                  </p>
                                  <span className="text-xs text-muted-foreground">
                                    {formatDate(reaction.createdAt)}
                                  </span>
                                </div>
                                <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-foreground">
                                  {reaction.text}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </main>
    </div>
  );
}
